import React from "react";
import { Dialog, DialogContent, DialogActions, Box, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

type DeleteConfirmationDialogProps = {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
};

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onCancel,
  onConfirm,
  title = "Are you sure you want to delete this item?",
  description = "Deleting this item will permanently remove all associated data. This action cannot be undone.",
  confirmText = "Yes, I’m sure",
  cancelText = "No, cancel",
}) => (
  <Dialog
    open={open}
    onClose={onCancel}
    PaperProps={{
      sx: {
        borderRadius: "16px",
        p: 4,
        minWidth: 420,
        maxWidth: 480,
        bgcolor: "#FFFEFA",
        position: "relative",
      },
    }}
  >
    <IconButton
      onClick={onCancel}
      sx={{
        position: "absolute",
        right: 16,
        top: 16,
        color: "#222",
      }}
      aria-label="Close"
    >
      <CloseIcon />
    </IconButton>
    <DialogContent sx={{ p: 0 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
        <Box
          sx={{
            background: "#FEFFF8",
            borderRadius: "50%",
            width: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <g clipPath="url(#clip0_450_3987)">
              <path
                d="M12 0C9.62663 0 7.30655 0.703788 5.33316 2.02236C3.35977 3.34094 1.8217 5.21508 0.913451 7.4078C0.00519941 9.60051 -0.232441 12.0133 0.230582 14.3411C0.693605 16.6689 1.83649 18.807 3.51472 20.4853C5.19295 22.1635 7.33115 23.3064 9.65892 23.7694C11.9867 24.2324 14.3995 23.9948 16.5922 23.0865C18.7849 22.1783 20.6591 20.6402 21.9776 18.6668C23.2962 16.6934 24 14.3734 24 12C23.9965 8.81847 22.7311 5.76826 20.4814 3.51858C18.2317 1.2689 15.1815 0.00349392 12 0ZM12 18C11.7627 18 11.5307 17.9296 11.3333 17.7978C11.136 17.6659 10.9822 17.4785 10.8913 17.2592C10.8005 17.0399 10.7768 16.7987 10.8231 16.5659C10.8694 16.3331 10.9837 16.1193 11.1515 15.9515C11.3193 15.7836 11.5331 15.6694 11.7659 15.6231C11.9987 15.5768 12.24 15.6005 12.4592 15.6913C12.6785 15.7822 12.8659 15.936 12.9978 16.1333C13.1296 16.3307 13.2 16.5627 13.2 16.8C13.2 17.1183 13.0736 17.4235 12.8485 17.6485C12.6235 17.8736 12.3183 18 12 18ZM13.2 13.2C13.2 13.5183 13.0736 13.8235 12.8485 14.0485C12.6235 14.2736 12.3183 14.4 12 14.4C11.6817 14.4 11.3765 14.2736 11.1515 14.0485C10.9264 13.8235 10.8 13.5183 10.8 13.2V7.2C10.8 6.88174 10.9264 6.57651 11.1515 6.35147C11.3765 6.12643 11.6817 6 12 6C12.3183 6 12.6235 6.12643 12.8485 6.35147C13.0736 6.57651 13.2 6.88174 13.2 7.2V13.2Z"
                fill="#E83F35"
              />
            </g>
            <defs>
              <clipPath id="clip0_450_3987">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </Box>
        <Box sx={{ fontSize: 16, color: "#000", fontWeight: 500, textAlign: "center" }}>
          <Box sx={{ mb: 2, fontWeight: 500 }}>{title}</Box>
          <div>{description}</div>
        </Box>
      </Box>
    </DialogContent>
    <DialogActions sx={{ justifyContent: "center", pb: 0 }}>
      <Button
        onClick={onConfirm}
        sx={{
          background: "#E83F35",
          color: "#fff",
          fontWeight: 700,
          borderRadius: "8px",
          px: 2,
          py: 0.8,
          "&:hover": {
            background: "#d13c3c",
          },
        }}
        variant="contained"
      >
        {confirmText}
      </Button>
      <Button
        onClick={onCancel}
        sx={{
          background: "#F8F8F9",
          color: "#67676C",
          fontWeight: 700,
          borderRadius: "8px",
          px: 2,
          py: 0.8,
          ml: 2,
          border: "1px solid #E0E0E4",
          "&:hover": {
            background: "#ececec",
          },
        }}
        variant="outlined"
      >
        {cancelText}
      </Button>
    </DialogActions>
  </Dialog>
);

export default DeleteConfirmationDialog;
